import { Loader2 } from "lucide-react";
import { useMemo, useState, useCallback } from "react";

import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import { CardsHeader } from "../components/CardsHeader";
import { CreateOrganizationModal } from "../components/CreateOrganizationModal";
import { DeleteOrganizationModal } from "../components/DeleteOrganizationModal";
import { DeleteOrganizationSuccessModal } from "../components/DeleteOrganizationSuccessModal";
import { OrganizationDetailModal } from "../components/OrganizationDetailModal";
import { OrganizationsPagination } from "../components/OrganizationsPagination";
import { OrganizationsTable } from "../components/OrganizationsTable";
import { OrganizationsTableHeader } from "../components/OrganizationsTableHeader";
import { UpdateOrganizationModal } from "../components/UpdateOrganizationModal";
import { useOrganizationsPage } from "../hooks/useOrganizationsPage";
import { OrganizationsService } from "../services/organizationsService";
import type { UpdateOrganizationRequest } from "../types/organization-form.types";

export const OrganizationsPage = () => {
  const { t } = useTranslations("cards");
  const { t: tGlobal } = useTranslations("global");
  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_section_gestion") },
      { label: tGlobal("sidebar_menu_cards"), to: "/app/cards" },
      { label: t("cards_tab_label_organizations"), to: "/app/cards/organizations" },
    ],
    [t, tGlobal],
  );
  useAppShellBreadcrumb(breadcrumb);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedUpdateOrganizationId, setSelectedUpdateOrganizationId] = useState<string | null>(null);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedDeleteOrganizationId, setSelectedDeleteOrganizationId] = useState<string | null>(null);
  const [selectedDeleteOrganizationName, setSelectedDeleteOrganizationName] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteSuccessModalOpen, setIsDeleteSuccessModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    organizations,
    totalItems,
    lastPage,
    currentPage,
    itemsPerPage,
    canGoPrevious,
    canGoNext,
    pageNumbers,
    paginationOptions,
    isLoading,
    error,
    handleItemsPerPageChange,
    handlePageChange,
    handleNewOrganizationClick,
    handleCreateOrganization,
    isCreateModalOpen,
    setIsCreateModalOpen,
    fetchOrganizations,
  } = useOrganizationsPage();

  const handleViewOrganizationDetail = (organizationId: string) => {
    setSelectedOrganizationId(organizationId);
    setIsDetailModalOpen(true);
  };

  const handleUpdateOrganizationClick = (organizationId: string) => {
    setSelectedUpdateOrganizationId(organizationId);
    setIsUpdateModalOpen(true);
  };

  const handleDeleteOrganizationClick = (organizationId: string) => {
    const organization = organizations.find((org) => org.id === organizationId);
    setSelectedDeleteOrganizationId(organizationId);
    setSelectedDeleteOrganizationName(organization?.name || null);
    setIsDeleteModalOpen(true);
  };

  const handleUpdateOrganization = useCallback(
    async (id: string, data: UpdateOrganizationRequest) => {
      setIsUpdating(true);
      try {
        await OrganizationsService.updateOrganization(id, data);
        colocaToast.success(t("organizations_success_update"));
        setIsUpdateModalOpen(false);
        setSelectedUpdateOrganizationId(null);
        await fetchOrganizations();
      } catch (err: unknown) {
        const errorMessage = HttpApiService.getErrorMessage(err) || t("organizations_error_update");
        throw new Error(errorMessage);
      } finally {
        setIsUpdating(false);
      }
    },
    [fetchOrganizations, t],
  );

  const handleDeleteOrganization = useCallback(async () => {
    if (!selectedDeleteOrganizationId) return;

    setIsDeleting(true);
    try {
      await OrganizationsService.deleteOrganization(selectedDeleteOrganizationId);
      setIsDeleteModalOpen(false);
      setIsDeleteSuccessModalOpen(true);
      await fetchOrganizations();
    } catch (err: unknown) {
      const errorMessage = HttpApiService.getErrorMessage(err) || t("organizations_error_delete");
      setIsDeleteModalOpen(false);
      setSelectedDeleteOrganizationId(null);
      setSelectedDeleteOrganizationName(null);
      console.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  }, [selectedDeleteOrganizationId, fetchOrganizations, t]);

  const handleCloseDeleteSuccessModal = useCallback(() => {
    setIsDeleteSuccessModalOpen(false);
    setSelectedDeleteOrganizationId(null);
    setSelectedDeleteOrganizationName(null);
  }, []);

  return (
    <div className="space-y-8 p-2">
      <CardsHeader activeTabId="organizations" />

      <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
        <OrganizationsTableHeader
          currentCount={organizations.length}
          totalCount={totalItems}
          onNewOrganizationClick={handleNewOrganizationClick}
        />

        <div className="mt-6">
          {error ? (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                <span>{error}</span>
              </div>
            </div>
          ) : organizations.length === 0 && !isLoading ? (
            <div className="bg-gray-50 dark:bg-zinc-800/60 border border-gray-200 dark:border-zinc-800 rounded-lg p-12 text-center">
              <p className="text-gray-500 dark:text-gray-400">{t("organizations_not_found")}</p>
            </div>
          ) : (
            <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-lg overflow-hidden relative">
              {isLoading && (
                <div className="absolute inset-0 bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm z-10 flex items-center justify-center">
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="h-8 w-8 animate-spin text-green-500" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {t("organizations_loading")}
                    </span>
                  </div>
                </div>
              )}

              <OrganizationsTable
                organizations={organizations}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                paginationOptions={paginationOptions}
                onItemsPerPageChange={handleItemsPerPageChange}
                onViewOrganizationDetail={handleViewOrganizationDetail}
                onUpdateOrganization={handleUpdateOrganizationClick}
                onDeleteOrganization={handleDeleteOrganizationClick}
              />

              <OrganizationsPagination
                currentPage={currentPage}
                lastPage={lastPage}
                canGoPrevious={canGoPrevious}
                canGoNext={canGoNext}
                isLoading={isLoading}
                pageNumbers={pageNumbers}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </section>

      <CreateOrganizationModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateOrganization}
        isLoading={isLoading}
      />

      <OrganizationDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedOrganizationId(null);
        }}
        organizationId={selectedOrganizationId}
      />

      <UpdateOrganizationModal
        isOpen={isUpdateModalOpen}
        onClose={() => {
          setIsUpdateModalOpen(false);
          setSelectedUpdateOrganizationId(null);
        }}
        onSubmit={handleUpdateOrganization}
        organizationId={selectedUpdateOrganizationId}
        isLoading={isUpdating}
      />

      <DeleteOrganizationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedDeleteOrganizationId(null);
          setSelectedDeleteOrganizationName(null);
        }}
        onConfirm={handleDeleteOrganization}
        organizationName={selectedDeleteOrganizationName || undefined}
        isLoading={isDeleting}
      />

      <DeleteOrganizationSuccessModal
        isOpen={isDeleteSuccessModalOpen}
        onClose={handleCloseDeleteSuccessModal}
      />
    </div>
  );
};
